import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, RefreshCw, Dices } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

export const RandomGeneratorTool: React.FC = () => {
  const { t } = useTranslation();
  const [numbers, setNumbers] = useState<number[]>([]);
  const [strings, setStrings] = useState<string[]>([]);
  const [numberMin, setNumberMin] = useState(1);
  const [numberMax, setNumberMax] = useState(100);
  const [numberCount, setNumberCount] = useState(10);
  const [stringLength, setStringLength] = useState(8);
  const [stringCount, setStringCount] = useState(10);

  const generateRandomNumbers = () => {
    const result: number[] = [];
    for (let i = 0; i < numberCount; i++) {
      const randomNum = Math.floor(Math.random() * (numberMax - numberMin + 1)) + numberMin;
      result.push(randomNum);
    }
    setNumbers(result);
  };

  const generateRandomStrings = () => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const result: string[] = [];
    
    for (let i = 0; i < stringCount; i++) {
      let randomString = '';
      for (let j = 0; j < stringLength; j++) {
        randomString += charset.charAt(Math.floor(Math.random() * charset.length));
      }
      result.push(randomString);
    }
    setStrings(result);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Dices className="w-5 h-5" />
            <span>{t('tools.randomGenerator.name')}</span>
          </CardTitle>
        </CardHeader>
      </Card>

      {/* Random Numbers */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.randomGenerator.numbers')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label={t('tools.randomGenerator.min')}
                type="number"
                value={numberMin}
                onChange={(e) => setNumberMin(parseInt(e.target.value) || 1)}
              />
              <Input
                label={t('tools.randomGenerator.max')}
                type="number"
                value={numberMax}
                onChange={(e) => setNumberMax(parseInt(e.target.value) || 100)}
              />
              <Input
                label={t('tools.randomGenerator.count')}
                type="number"
                min="1"
                max="1000"
                value={numberCount}
                onChange={(e) => setNumberCount(Math.max(1, Math.min(1000, parseInt(e.target.value) || 10)))}
              />
            </div>
            
            <div className="flex gap-2">
              <Button onClick={generateRandomNumbers}>
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('tools.randomGenerator.generateNumbers')}
              </Button>
              {numbers.length > 0 && (
                <Button
                  onClick={() => copyToClipboard(numbers.join(', '))}
                  variant="outline"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  {t('tools.randomGenerator.copyAll')}
                </Button>
              )}
            </div>

            {numbers.length > 0 && (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded border">
                <div className="flex flex-wrap gap-2">
                  {numbers.map((num, index) => (
                    <button
                      key={index}
                      onClick={() => copyToClipboard(num.toString())}
                      className="px-3 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 font-mono text-sm"
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Random Strings */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.randomGenerator.strings')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('tools.randomGenerator.stringLength')}
                type="number"
                min="1"
                max="100"
                value={stringLength}
                onChange={(e) => setStringLength(Math.max(1, Math.min(100, parseInt(e.target.value) || 8)))}
              />
              <Input
                label={t('tools.randomGenerator.count')}
                type="number"
                min="1"
                max="100"
                value={stringCount}
                onChange={(e) => setStringCount(Math.max(1, Math.min(100, parseInt(e.target.value) || 10)))}
              />
            </div>
            
            <div className="flex gap-2">
              <Button onClick={generateRandomStrings}>
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('tools.randomGenerator.generateStrings')}
              </Button>
              {strings.length > 0 && (
                <Button
                  onClick={() => copyToClipboard(strings.join('\n'))}
                  variant="outline"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  {t('tools.randomGenerator.copyAll')}
                </Button>
              )}
            </div>

            {strings.length > 0 && (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded border">
                <div className="space-y-2">
                  {strings.map((str, index) => (
                    <button
                      key={index}
                      onClick={() => copyToClipboard(str)}
                      className="block w-full text-left px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 font-mono text-sm"
                    >
                      {str}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
